/* import __COLOCATED_TEMPLATE__ from './dashboard-web-mount.hbs'; */
var _class, _descriptor, _descriptor2, _descriptor3, _descriptor4, _descriptor5, _descriptor6, _descriptor7;
function _initializerDefineProperty(e, i, r, l) { r && Object.defineProperty(e, i, { enumerable: r.enumerable, configurable: r.configurable, writable: r.writable, value: r.initializer ? r.initializer.call(l) : void 0 }); }
function _defineProperty(e, r, t) { return (r = _toPropertyKey(r)) in e ? Object.defineProperty(e, r, { value: t, enumerable: !0, configurable: !0, writable: !0 }) : e[r] = t, e; }
function _toPropertyKey(t) { var i = _toPrimitive(t, "string"); return "symbol" == typeof i ? i : i + ""; }
function _toPrimitive(t, r) { if ("object" != typeof t || !t) return t; var e = t[Symbol.toPrimitive]; if (void 0 !== e) { var i = e.call(t, r || "default"); if ("object" != typeof i) return i; throw new TypeError("@@toPrimitive must return a primitive value."); } return ("string" === r ? String : Number)(t); }
function _applyDecoratedDescriptor(i, e, r, n, l) { var a = {}; return Object.keys(n).forEach(function (i) { a[i] = n[i]; }), a.enumerable = !!a.enumerable, a.configurable = !!a.configurable, ("value" in a || a.initializer) && (a.writable = !0), a = r.slice().reverse().reduce(function (r, n) { return n(i, e, r) || r; }, a), l && void 0 !== a.initializer && (a.value = a.initializer ? a.initializer.call(l) : void 0, a.initializer = void 0), void 0 === a.initializer ? (Object.defineProperty(i, e, a), null) : a; }
function _initializerWarningHelper(r, e) { throw Error("Decorating class property failed. Please ensure that transform-class-properties is enabled and runs after the decorators transform."); }
import Ember from 'ember';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { action } from '@ember/object';
import WritableDOMStream from 'writable-dom';
import * as Sentry from '@sentry/ember';
import { inject as service } from '@ember/service';
import { getLocationHref, getUrlHash, getUrlPathname, getUrlSearch, isEmberHost } from '@square/dashboard-utilities/utils/window';
import { datadogRum } from '@datadog/browser-rum';

// Helpers to disable test mocking for tests that explicitly need to verify the
// behavior of streaming the dashboard-web application.
let isEnabledInTests = false;
export function enableInTests() {
  isEnabledInTests = true;
}
export function disableInTests() {
  isEnabledInTests = false;
}

// The name of the event that is emitted when the application is ready to hydrate.
const APP_READY_EVENT = 'dashboard-web:ready';

// The header that should be used to route to the application.
const APP_HEADER = 'x-dashboard-web';

// The origin of the dashboard-web (in square-web) app when running locally.
const DASHBOARD_WEB_LOCAL_ORIGIN = 'http://localhost:5200';
// The application can only be initialized once in a window context, so it's stored as a module variable.
let app;
export function setApp(app_) {
  app = app_;
}
export function resetApp() {
  app = undefined;
}
function getReactRouter() {
  return window.__reactRouterDataRouter;
}
let isFetchPatched = false;
let originalFetch = window.fetch;

/**
 * Patches the fetch function so that all React Router requests to the manifest
 * and loaders are sent with the X-Dashboard-Web header so that they are routed
 * properly via tracon. For local integration mode against staging, we need to
 * change the origin of the request to the local dashboard-web app.
 */
function patchFetch(mode) {
  if (isFetchPatched) {
    return;
  }
  originalFetch = window.fetch;
  // eslint-disable-next-line dashboard/use-utilities-window
  window.fetch = (...args) => {
    const request = new Request(...args);
    const url = new URL(request.url);
    if (url.pathname === '/dashboard/__manifest' || url.pathname.endsWith('.data')) {
      request.headers.append('X-Dashboard-Web', 'true');
      if (mode === 'local') {
        return originalFetch(new Request(`${DASHBOARD_WEB_LOCAL_ORIGIN}${url.pathname}${url.search}`, request));
      }
    }
    return originalFetch(request);
  };
  isFetchPatched = true;
}
export function resetFetchPatch() {
  // eslint-disable-next-line dashboard/use-utilities-window
  window.fetch = originalFetch;
  isFetchPatched = false;
}
let DashboardWebMount = (_class = class DashboardWebMount extends Component {
  get shouldShowLoader() {
    const showLoader = this.args.showLoader ?? true; // Default to true to maintain existing behavior
    return this.isLoading && showLoader;
  }
  constructor(owner, args) {
    super(owner, args);
    _initializerDefineProperty(this, "environment", _descriptor, this);
    _initializerDefineProperty(this, "router", _descriptor2, this);
    _initializerDefineProperty(this, "navigationShim", _descriptor3, this);
    _initializerDefineProperty(this, "isLoading", _descriptor4, this);
    _initializerDefineProperty(this, "isError", _descriptor5, this);
    _initializerDefineProperty(this, "isTesting", _descriptor6, this);
    _initializerDefineProperty(this, "cookies", _descriptor7, this);
    _defineProperty(this, "root", void 0);
    _defineProperty(this, "handleRouteChange", void 0);
    this.isTesting = Ember.testing && !isEnabledInTests;
  }
  willDestroy() {
    super.willDestroy();

    // Only remove listener if it was actually added
    if (this.handleRouteChange) {
      // https://api.emberjs.com/ember/4.12/classes/RouterService/methods/off?anchor=off
      // @ts-expect-error Property 'off' exists at runtime but not in type definitions
      this.router.off('routeDidChange', this.handleRouteChange);
    }
  }
  addRouteChangeListener() {
    // Don't add listener if component is already destroyed
    if (this.isDestroyed || this.isDestroying) {
      return;
    }

    // This is a workaround to ensure that dashboard-web navigates to the
    // correct route when seller navigates to a new route from Ember link, which
    // might not trigger React Router's route change given Ember Router doesn't
    // use History API or PopStateEvent, and the route change might not be
    // detected by React Router.
    this.handleRouteChange = () => this.syncEmberToReactRouter();
    this.router.on('routeDidChange', this.handleRouteChange);
  }

  /**
   * Sets up event listener for module initialization
   */
  waitForAppInitialization() {
    return new Promise((resolve, reject) => {
      window.addEventListener(APP_READY_EVENT, event => {
        const app = event.detail;
        if (app) {
          setApp(app);
          resolve(app);
        } else {
          reject(new Error('Received invalid app data'));
        }
      }, {
        once: true
      });
    });
  }
  async shouldLoadLocalMode() {
    const urlParams = new URL(getLocationHref()).searchParams;
    const useLocal = urlParams.get('localMode') || urlParams.get('localmode');

    // If explicitly requested via query param and in staging, use local mode
    if (useLocal && this.environment.isStaging) {
      return true;
    }
    if (!this.environment.isDevelopment) {
      return false;
    }
    const isLocalServerAvailable = await this.isLocalServerAvailable();
    return isLocalServerAvailable;
  }
  async isLocalServerAvailable() {
    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 1000); // 1 second timeout

      const response = await fetch(`${DASHBOARD_WEB_LOCAL_ORIGIN}/dashboard/__manifest`, {
        signal: controller.signal,
        method: 'GET'
      });
      clearTimeout(timeoutId);
      return response.ok;
    } catch {
      return false;
    }
  }
  getAppUrl(mode) {
    const href = getLocationHref();

    // This allow Dashboard to load dashboard-web locally when running in staging
    if (mode === 'local') {
      const url = new URL(href);
      return `${DASHBOARD_WEB_LOCAL_ORIGIN}${url.pathname}${url.search}`;
    }
    return href;
  }
  async loadCookiesIfNecessary(mode) {
    if (mode !== 'local' || !this.environment.isStaging && !this.environment.isDevelopment) {
      return null;
    }
    if (this.cookies) {
      return this.cookies;
    }
    const response = await fetch('/dashboard/_cookies');
    if (!response.ok) {
      throw new Error(`Failed to fetch cookies: ${response.status} ${response.statusText}`);
    }
    this.cookies = await response.json();
    return this.cookies;
  }
  formatCookiesForHeader(cookies) {
    return Object.entries(cookies).map(([key, value]) => `${key}=${value}`).join('; ');
  }

  /**
   * Streams the application content from a SSR-rendered page into the specified
   * DOM element. Returns a promise that resolves when the application is ready.
   */
  async streamAppToElement(domElement, mode = 'default') {
    const appPromise = this.waitForAppInitialization();
    patchFetch(mode);
    const cookies = await this.loadCookiesIfNecessary(mode);
    const headers = {
      [APP_HEADER]: 'true'
    };
    if (cookies) {
      headers['x-integration-mode-cookies'] = this.formatCookiesForHeader(cookies);
    }
    const response = await fetch(this.getAppUrl(mode), {
      headers
    });
    if (!response.ok) {
      throw new Error(`Failed to fetch content: ${response.status} ${response.statusText}`);
    }
    const body = response.body;
    if (!body) {
      throw new Error('Response body is null');
    }
    await body.pipeThrough(new TextDecoderStream()).pipeTo(new WritableDOMStream(domElement));
    return appPromise;
  }

  /**
   * Syncs the current Ember route to React Router.
   */
  async syncEmberToReactRouter() {
    const reactRouter = getReactRouter();
    if (!reactRouter) {
      return;
    }
    const windowPathname = getUrlPathname();
    const windowQueryString = getUrlSearch();
    const windowHash = getUrlHash();
    const windowLocation = `${windowPathname}${windowQueryString}${windowHash}`;
    const reactRouterLocation = `${reactRouter.state.location.pathname}${reactRouter.state.location.search}${reactRouter.state.location.hash}`;

    // Prevent infinite navigation loops by only syncing if the current window location and React Router locations differ.
    // single-spa fires a popstate event when React Router navigates. That popstate event can cause
    // Ember's router to transition the route, which in turn calls this sync function again.
    if (windowLocation !== reactRouterLocation) {
      // React Router's internal state includes the basename, but it expects paths _not_
      // to have the basename when calling navigate.
      const pathWithoutBasename = windowPathname.replace(/^\/dashboard/, '');
      const path = `${pathWithoutBasename}${windowQueryString}${windowHash}`;
      await this.navigationShim.executeWithOriginalHistory(() => reactRouter.navigate(path || '/', {
        replace: true
      }));
    }
  }

  /**
   * Initializes or re-mounts the application.
   */
  async setupApp(domElement) {
    if (app) {
      await this.syncEmberToReactRouter();
      this.root = await app.renderRoot(domElement);
    } else {
      const shouldLoadLocal = await this.shouldLoadLocalMode();
      const app = await this.streamAppToElement(domElement, shouldLoadLocal ? 'local' : 'default');
      this.root = await app.hydrateRoot(domElement);
    }
    // Help distinguish between ember versions of routes and React versions
    datadogRum.setViewContextProperty('reactType', 'dashboard-web');
  }

  /**
   * Handles and reports mounting errors.
   */
  handleMountError(error) {
    Sentry.withScope(scope => {
      scope.setExtra('feature', 'dashboard-web-mount');
      Sentry.captureException(error);
    });
    this.isError = true;
  }
  async mount(domElement) {
    try {
      if (isEmberHost()) {
        await this.setupApp(domElement);
      } else {
        // When mounted inside of the dashboard-web host, we just need to tell it to render this route,
        // and avoid re-embedding the dashboard-web application a second time.
        await this.syncEmberToReactRouter();
      }

      // Set up the route listener after initial setup is complete to prevent race conditions double-invoking
      // the react-router navigate function.
      this.addRouteChangeListener();
    } catch (error) {
      this.handleMountError(error);
    } finally {
      this.isLoading = false;
    }
  }
  teardown() {
    this.root?.unmount();
    this.root = undefined;
  }
}, _descriptor = _applyDecoratedDescriptor(_class.prototype, "environment", [service], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: null
}), _descriptor2 = _applyDecoratedDescriptor(_class.prototype, "router", [service], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: null
}), _descriptor3 = _applyDecoratedDescriptor(_class.prototype, "navigationShim", [service], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: null
}), _descriptor4 = _applyDecoratedDescriptor(_class.prototype, "isLoading", [tracked], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: function () {
    return true;
  }
}), _descriptor5 = _applyDecoratedDescriptor(_class.prototype, "isError", [tracked], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: function () {
    return false;
  }
}), _descriptor6 = _applyDecoratedDescriptor(_class.prototype, "isTesting", [tracked], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: function () {
    return false;
  }
}), _descriptor7 = _applyDecoratedDescriptor(_class.prototype, "cookies", [tracked], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: function () {
    return null;
  }
}), _applyDecoratedDescriptor(_class.prototype, "mount", [action], Object.getOwnPropertyDescriptor(_class.prototype, "mount"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "teardown", [action], Object.getOwnPropertyDescriptor(_class.prototype, "teardown"), _class.prototype), _class);
export { DashboardWebMount as default };